import { useCallback, useEffect, useRef, useState } from 'react';
import type { Point } from './toPortInfo.tsx';
import type { ViewBoxRect } from './ListEdgeDetails.tsx';
import { calculateViewBox } from './isEveryPropertyDefined.tsx';
import { useLatest } from './useLatest.tsx';
import useResizeObserver from './useResizeObserver.tsx';
import { getInitialSize } from './getCanvasBoundingBox.tsx';

export const useCanvasView = (
	zoom: number,
	pan: Point,
	onViewBoxChange?: (viewBox: ViewBoxRect) => void,
) => {
	const canvasContainerRef = useRef<HTMLDivElement>(null);

	const [diagramSize, setDiagramSize] = useState(getInitialSize());
	const [viewBox, setViewBox] = useState<ViewBoxRect>({ width: 0, height: 0, top: 0, left: 0 });

	const onViewBoxChangeLatest = useLatest(onViewBoxChange);

	const updateViewBox = useCallback(
		(box: ViewBoxRect) => {
			setViewBox(box);
			onViewBoxChangeLatest.current?.(box);
		},
		[onViewBoxChangeLatest],
	);

	const observerCanvasSize = useCallback(
		(entry: ResizeObserverEntry) => {
			const {
				contentRect: { width, height },
			} = entry;

			setDiagramSize({
				width,
				height,
			});

			updateViewBox(calculateViewBox({ width, height }, pan, zoom));
		},
		[pan, updateViewBox, zoom],
	);

	const { setRef: setCanvasContainerRef } = useResizeObserver(observerCanvasSize);

	useEffect(() => {
		setCanvasContainerRef(canvasContainerRef.current);
	}, [setCanvasContainerRef]);

	return {
		canvasContainerRef,
		diagramSize,
		viewBox,
	};
};
